/**
 * Client-side error telemetry (spec 037-improved-monitoring-debugging).
 *
 * Captures frontend crashes and client-side integration failures and POSTs them
 * to the backend `/telemetry/errors` endpoint, which logs them into GCP Cloud
 * Logging alongside server logs (no third-party telemetry SaaS).
 *
 * Hardened against telemetry storms: it de-duplicates identical reports within a
 * short window and never reports failures of the telemetry endpoint itself, so a
 * down backend cannot trigger an infinite reporting loop.
 */
import { appConfig } from '../config';

export type ClientErrorKind = 'vue' | 'window' | 'promise' | 'http';

export interface ClientErrorReport {
  message: string;
  stack?: string;
  component?: string;
  kind?: ClientErrorKind;
  /** Server distributed-trace id, forwarded from an error response's X-Trace-ID header. */
  trace_id?: string;
  /** Server span id, forwarded from an error response's X-Span-ID header. */
  span_id?: string;
}

interface ClientErrorPayload extends ClientErrorReport {
  url: string;
  session_id: string | null;
  browser: {
    user_agent: string;
    language: string;
    screen_size: string;
    platform: string;
  };
  timestamp: string;
}

const SESSION_KEY = 'cpt-sessionId';
const DEDUPE_WINDOW_MS = 10_000;

/** The fully-qualified telemetry endpoint URL (apiBaseUrl + telemetryPath). */
export function telemetryEndpoint(): string {
  return `${appConfig.apiBaseUrl}${appConfig.telemetryPath}`;
}

/** Skip reporting our own telemetry traffic to avoid recursive loops. */
export function isTelemetryUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  return url.includes(appConfig.telemetryPath);
}

function getSessionId(): string | null {
  try {
    const raw =
      sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  } catch {
    return null;
  }
}

function buildPayload(report: ClientErrorReport): ClientErrorPayload {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const scr = typeof screen !== 'undefined' ? screen : ({ width: 0, height: 0 } as Screen);
  return {
    message: String(report.message ?? '').slice(0, 4000),
    stack: report.stack ? String(report.stack).slice(0, 16000) : undefined,
    component: report.component,
    kind: report.kind,
    // Server-issued trace ids forwarded from an error response (omitted when absent).
    trace_id: report.trace_id,
    span_id: report.span_id,
    url: typeof window !== 'undefined' ? window.location.href : '',
    session_id: getSessionId(),
    browser: {
      user_agent: nav.userAgent || '',
      language: nav.language || '',
      screen_size: `${scr.width || 0}x${scr.height || 0}`,
      platform: (nav as Navigator).platform || '',
    },
    timestamp: new Date().toISOString(),
  };
}

// De-dupe identical reports fired in a tight loop (e.g. render error storms).
const recent = new Map<string, number>();

function shouldSend(payload: ClientErrorPayload): boolean {
  const key = `${payload.kind}:${payload.message}:${payload.component ?? ''}`;
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recent.set(key, now);
  // Bound the map so it cannot grow unbounded over a long session.
  if (recent.size > 50) {
    const cutoff = now - DEDUPE_WINDOW_MS;
    for (const [k, t] of recent) if (t < cutoff) recent.delete(k);
  }
  return true;
}

/**
 * Report a client-side error to the backend telemetry endpoint.
 *
 * @param report  the error details (message required; stack/component/kind optional)
 * @param useBeacon  when true (page unloading) use navigator.sendBeacon for durability
 */
export function sendErrorTelemetry(report: ClientErrorReport, useBeacon = false): void {
  try {
    const payload = buildPayload(report);
    if (isTelemetryUrl(payload.url)) return;
    if (!shouldSend(payload)) return;

    const endpoint = telemetryEndpoint();
    const body = JSON.stringify(payload);

    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
        return;
      } catch {
        // fall through to fetch
      }
    }

    // Fire-and-forget; swallow all errors so telemetry never masks the original
    // failure or recurses on its own network error.
    void fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      /* never report telemetry-endpoint failures */
    });
  } catch {
    /* telemetry must never throw into the caller */
  }
}

/** Test helper: clear the de-dupe cache between cases. */
export function _resetTelemetryDedupe(): void {
  recent.clear();
}
